import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { queryKeys } from "@/lib/queryKeys";
import * as JobMessageServiceNS from "@/services/job-message.service";
import { CHAT_REALTIME_PATCHING_ENABLED, SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";
import type { JobMessage, MessageCategory, SenderRole, MessagePriority } from "@/data/jobMessageTypes";
import type { JobMessageRow } from "@/services/job-message.service";

const mapRowToUi = JobMessageServiceNS.toUiJobMessage ?? (() => {
  // Fallback mapper: used only if the canonical export is missing
  return function fallbackMapRowToUi(row: JobMessageRow): JobMessage {
    return {
      id: row.id,
      jobId: row.job_id ?? "",
      body: row.body,
      category: (row.category as MessageCategory) ?? "general",
      senderId: row.sender_id,
      senderName: row.sender_name,
      senderRole: row.sender_role as SenderRole,
      timestamp: row.timestamp,
      priority: row.priority as MessagePriority,
      recipientIds: row.recipient_ids ?? [],
      recipientStatuses: row.recipient_statuses ?? {},
      mentionUserIds: row.mention_user_ids ?? undefined,
      context: row.context ?? undefined,
      relatedEventId: row.related_event_id ?? undefined,
    };
  };
})();

export function useJobMessages(
  jobId: string | undefined,
  category: MessageCategory = "general",
) {
  const queryClient = useQueryClient();

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.jobMessages(jobId, category),
    queryFn: () => {
      if (!jobId) return [];
      return JobMessageServiceNS.JobMessageService.getByJobAndCategory(jobId, category).then(
        (res) => res.data ?? [],
      );
    },
    enabled: !!jobId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!jobId) return;

    const channel = supabase
      .channel(`job-messages:${jobId}:${category}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "job_messages",
          filter: `job_id=eq.${jobId}`,
        },
        (payload) => {
          if (!CHAT_REALTIME_PATCHING_ENABLED) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.jobMessages(jobId, category),
            });
            return;
          }

          queryClient.setQueryData<JobMessage[] | null>(
            queryKeys.jobMessages(jobId, category),
            (old) => {
              const evt = String(payload.eventType ?? "").toUpperCase();
              const newRow = payload.new as unknown as JobMessageRow;
              const oldRow = payload.old as unknown as JobMessageRow;
              const newMsg = newRow ? mapRowToUi(newRow) : undefined;
              const oldMsg = oldRow ? mapRowToUi(oldRow) : undefined;

              if (evt === "INSERT" && newMsg) {
                const exists = old?.some((m) => m.id === newMsg.id) ?? false;
                return exists
                  ? old!.map((m) => (m.id === newMsg.id ? newMsg : m)).sort(
                      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
                    )
                  : [...(old ?? []), newMsg].sort(
                      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
                    );
              }
              if (evt === "UPDATE" && newMsg) {
                return (old ?? []).map((m) =>
                  m.id === newMsg.id ? { ...m, ...newMsg } : m,
                );
              }
              if (evt === "DELETE" && oldMsg) {
                return (old ?? []).filter((m) => m.id !== oldMsg.id);
              }
              return old ?? [];
            },
          );

          recordRealtimeMetric({
            source: "useJobMessages",
            event: "patch",
            table: "job_messages",
            entityId: String((payload.new as any)?.id ?? (payload.old as any)?.id ?? ""),
            detail: SUPABASE_SYNC_DEBUG
              ? { eventType: payload.eventType, category }
              : undefined,
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [jobId, category, queryClient]);

  return {
    messages: (data ?? []) as JobMessage[],
    error,
    isLoading,
    refetch,
  };
}
